import { useEffect, useState, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import ReactGA from 'react-ga';
import './App.css';
import { decodeInvite } from './Components/InviteCodec';

import PhoneNumberForWaiting from "./Pages/Waiting-list/Step1-PhoneNumberForWaiting";
import ConfirmedWaiting from "./Pages/Waiting-list/Step2-ConfirmedWaiting";
import WhoInvitedMe from "./Pages/Join/Step1-WhoInvitedMe"
import GetInvited from './Pages/Join/Step2-GetInvited';
import ConfirmedInvitation from './Pages/Join/Step3-ConfirmedInvitation';

const LOCAL_STORAGE_KEY_INVITER_PHONE_NUMBER = 'h.invpnumb';
const LOCAL_STORAGE_KEY_INVITE_CODE = 'h.invitec';
const LOCAL_STORAGE_KEY_MODE = 'h.mode';
const LOCAL_STORAGE_KEY_PHONE_NUMBER = 'h.pnumb';
// 🔹 NYTT: key för attrib-data i sessionStorage
const SESSION_STORAGE_KEY_ATTRIBUTION = 'h.attr';
// 🔹 Resolvd landningsvariant (från Step 1) → läses i Step 2 för variant-copy
const SESSION_STORAGE_KEY_LANDING_VARIANT = 'h.lv';
// 🔹 NYTT: stabilt session id (kan användas som input till event_id på servern)
const SESSION_STORAGE_KEY_SESSION_ID = 'h.sid';

ReactGA.initialize(process.env.REACT_APP_MEASUREMENT_ID);

// Hjälpare
const sanitizeCode = (v) => (v || "").toString().toUpperCase().replace(/\s+/g, "");
const asString = (v) => (typeof v === 'string' ? v : v ? String(v) : '');
const toE164SE = (raw) => {
  const s = asString(raw).trim();
  const pn = parsePhoneNumberFromString(s, 'SE');
  return pn?.isValid() ? pn.number : '';
};
// 🔹 Cookie helper
const readCookie = (name) => {
  try {
    const cookie = document.cookie || '';
    const match = cookie.split('; ').find((row) => row.startsWith(`${name}=`));
    return match ? (match.split('=')[1] || '') : '';
  } catch {
    return '';
  }
};

// 🔹 Build fbc from fbclid if needed (Meta-format: fb.1.<ts>.<fbclid>)
const buildFbcFromFbclid = (fbclid, tsSeconds) => {
  if (!fbclid) return '';
  const t = Number.isFinite(tsSeconds) ? tsSeconds : Math.floor(Date.now() / 1000);
  return `fb.1.${t}.${fbclid}`;
};

// 🔹 Ensure a stable session id
const ensureSessionId = () => {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY_SESSION_ID);
    if (existing) return existing;

    const sid = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    sessionStorage.setItem(SESSION_STORAGE_KEY_SESSION_ID, sid);
    return sid;
  } catch {
    // if sessionStorage blocked
    return '';
  }
};

// 🔹 Fånga UTM/fbclid/_fbp/_fbc + initial URL + UA/referrer → spara i sessionStorage
const captureAttributionFromUrl = () => {
  try {
    // Om vi redan har sparat första touch, skriv inte över (behåll första landning)
    const existingRaw = sessionStorage.getItem(SESSION_STORAGE_KEY_ATTRIBUTION);
    if (existingRaw) {
      // Men säkerställ att session_id finns
      ensureSessionId();
      return;
    }

    const url = new URL(window.location.href);
    const params = url.searchParams;

    const utm_source   = params.get('utm_source')   || '';
    const utm_medium   = params.get('utm_medium')   || '';
    const utm_campaign = params.get('utm_campaign') || '';
    const utm_content  = params.get('utm_content')  || '';
    const utm_term     = params.get('utm_term')     || '';
    const fbclid       = params.get('fbclid')       || '';

    // Läs Meta cookies om de finns
    const fbpCookie = readCookie('_fbp');
    const fbcCookie = readCookie('_fbc');

    const eventTimeSeconds = Math.floor(Date.now() / 1000);
    const fbp = fbpCookie || '';
    const fbc = fbcCookie || buildFbcFromFbclid(fbclid, eventTimeSeconds);

    const initialLandingUrl = window.location.href;
    const referrer = document.referrer || '';
    const userAgent = navigator.userAgent || '';
    const acceptLanguage = navigator.language || '';
    const tzOffsetMin = new Date().getTimezoneOffset(); // minuter
    const screenInfo = {
      w: window.screen?.width || null,
      h: window.screen?.height || null,
      dpr: window.devicePixelRatio || null,
    };

    // enkel heuristik: Meta-trafik om utm_source = facebook/instagram eller fbclid finns
    const isMetaTraffic =
      ['facebook', 'instagram'].includes(utm_source.toLowerCase()) || !!fbclid;

    const trafficType = isMetaTraffic ? 'meta_ad' : 'unknown';

    const session_id = ensureSessionId();

    const attribution = {
      // UTM
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      utm_term,

      // Meta click identifiers
      fbclid,
      fbp,
      fbc,

      // Source context
      initialLandingUrl,
      referrer,

      // Client hints (useful for server-side CAPI as client_user_agent etc.)
      userAgent,
      acceptLanguage,
      tzOffsetMin,
      screenInfo,

      // meta flags
      isMetaTraffic,
      trafficType,

      // stable session id (for event_id/dedupe correlation server-side)
      session_id,

      // bookkeeping
      firstSeenTs: eventTimeSeconds,
    };

    sessionStorage.setItem(SESSION_STORAGE_KEY_ATTRIBUTION, JSON.stringify(attribution));
  } catch (e) {
    console.warn('Failed to capture attribution', e);
  }
};

// Strippa invite från URL och lämna kvar övriga ev. parametrar (t.ex. utm_*)
const cleanInviteFromUrl = () => {
  const u = new URL(window.location.href);
  // byt alltid till /app som path
  u.pathname = '/app';
  // ta bort legacy query-params
  u.searchParams.delete('friend');
  u.searchParams.delete('code');
  u.searchParams.delete('i');
  window.history.replaceState({}, "", u.pathname + u.search);
};

function App() {
  const location = useLocation();

  // 1) Läs URL: både query OCH path (för bakåtkompabilitet + nya format)
  const { friendFromUrl, codeFromUrl, friendTokenPresent, decodingFriendSuccess } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const friendQ = params.get("friend");         // legacy: ?friend=07...
    const codeQ   = params.get("code") || params.get("i"); // legacy: ?code= / nytt: ?i=

    // Path-format: /f/07xxxxxxx och /i/ABCD123
    // OBS: matchar endast när path inleds med rätt segment.
    const path = location.pathname || "";
    const friendPCoded = path.match(/^\/f\/([^/?#]+)/)?.[1] || null;
    const { ok: decodingFSucceeded, value: friendP } = decodeInvite(friendPCoded);
    const codeP   = path.match(/^\/i\/([^/?#]+)/)?.[1] || null;

    // Prioritet: path > query (om båda råkar finnas)
    return {
      friendFromUrl: friendP ?? friendQ,
      decodingFriendSuccess: decodingFSucceeded,
      friendTokenPresent: Boolean(friendPCoded || friendQ),
      codeFromUrl: codeP ?? codeQ,
    };
  }, [location.pathname, location.search]);

  // 2) States
  const [initialized, setInitialized] = useState(false)
  const [inviterPhoneNumber, setInviterPhoneNumber] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [mode, setMode] = useState("code");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [inviterResolvedFromParams, setInviterResolvedFromParams] = useState(false);
  const [codeResolvedFromParams, setCodeResolvedFromParams] = useState(false);
  const [inviteLinkError, setInviteLinkError] = useState(false); 

  // 3) Page tracking
  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, [location.pathname, location.search]);

  // 4) Init med PRIORITET: URL (path/query) 
  useEffect(() => {
    if (initialized) return
    setInitialized(true)
    
    // 🔹 NYTT: fånga attribution EN gång direkt vid init
    captureAttributionFromUrl();

    // Path/Query innehåller code/i → kör code-läge
    if (codeFromUrl) {
      const code = sanitizeCode(codeFromUrl);
      setMode("code");
      setInviteCode(code);
      setCodeResolvedFromParams(true);
      setInviterResolvedFromParams(false);
      cleanInviteFromUrl();
      return;
    }

    // Path/Query innehåller friend → kör friend-läge
    if (friendFromUrl) {
      const e164 = toE164SE(friendFromUrl);
      setMode("friend");
      if (e164) {
        setInviterPhoneNumber(e164);
        setInviterResolvedFromParams(true);
        setCodeResolvedFromParams(false);
        setInviteLinkError(false);
      } else {
        // decode lyckades men numret var ogiltigt / ej normaliserbart
        setInviterPhoneNumber("");
        setInviterResolvedFromParams(false);
        setCodeResolvedFromParams(false);
        setInviteLinkError(true);
      }
      // städa URL: ersätt /f/... (eller query) med /app och plocka bort invite-parametrar
      cleanInviteFromUrl();
    }

    // Om vi försökte läsa token men decode failade
    if (!decodingFriendSuccess && friendTokenPresent) {
      setInviteLinkError(true);
      setMode('friend')
    } else {
      setInviteLinkError(false);
    }    
  }, [friendFromUrl, codeFromUrl, decodingFriendSuccess, friendTokenPresent, initialized]);

  if (!initialized) return null

  return (
    <Routes>
      {/* Redirect från "/" till "/app" */}
      <Route path="/" element={<Navigate to="/app" replace />} />

      {/* Waiting list */}
      <Route path="/waiting-list" element={
        <PhoneNumberForWaiting
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          LOCAL_STORAGE_KEY_PHONE_NUMBER={LOCAL_STORAGE_KEY_PHONE_NUMBER}
        />
      } />
      <Route path="/confirmed-waiting" element={<ConfirmedWaiting />} />

      {/* Nya path-formaten (SPA): låt dem landa här så useEffect ovan kan fånga & rensa URL */}
      <Route path="/f/:friend" element={<Navigate to="/app" replace />} />
      <Route path="/i/:code" element={<Navigate to="/app" replace />} />

      {/* Invite flow */}
      <Route path="/app" element={
        <WhoInvitedMe
          inviterPhoneNumber={inviterPhoneNumber}
          setInviterPhoneNumber={setInviterPhoneNumber}
          inviteLinkError={inviteLinkError}
          inviteCode={inviteCode}
          setInviteCode={setInviteCode}
          mode={mode}
          setMode={setMode}
          LOCAL_STORAGE_KEY_INVITER_PHONE_NUMBER={LOCAL_STORAGE_KEY_INVITER_PHONE_NUMBER}
          LOCAL_STORAGE_KEY_INVITE_CODE={LOCAL_STORAGE_KEY_INVITE_CODE}
          LOCAL_STORAGE_KEY_MODE={LOCAL_STORAGE_KEY_MODE}
          SESSION_STORAGE_KEY_ATTRIBUTION={SESSION_STORAGE_KEY_ATTRIBUTION}
          SESSION_STORAGE_KEY_LANDING_VARIANT={SESSION_STORAGE_KEY_LANDING_VARIANT}
          inviterResolvedFromParams={inviterResolvedFromParams}
          setInviterResolvedFromParams={setInviterResolvedFromParams}
          codeResolvedFromParams={codeResolvedFromParams}
          setCodeResolvedFromParams={setCodeResolvedFromParams}
        />
      } />
      <Route path="/app/complete-your-invite" element={
        <GetInvited
          inviterPhoneNumber={inviterPhoneNumber}
          inviteCode={inviteCode}
          mode={mode}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          LOCAL_STORAGE_KEY_INVITER_PHONE_NUMBER={LOCAL_STORAGE_KEY_INVITER_PHONE_NUMBER}
          LOCAL_STORAGE_KEY_INVITE_CODE={LOCAL_STORAGE_KEY_INVITE_CODE}
          LOCAL_STORAGE_KEY_MODE={LOCAL_STORAGE_KEY_MODE}
          SESSION_STORAGE_KEY_ATTRIBUTION={SESSION_STORAGE_KEY_ATTRIBUTION}
          SESSION_STORAGE_KEY_LANDING_VARIANT={SESSION_STORAGE_KEY_LANDING_VARIANT}
        />
      } />
      <Route path="/app/invite-completed" element={<ConfirmedInvitation />} />
    </Routes>
  );
}

export default App;
